import type { CurrentUser } from '@zhiyue/core';
import { createAppRequest } from '@/services/request';

const OPEN_PLATFORM_TOKEN_STORAGE_KEY = 'openPlatformAccessToken';
const TOKEN_REFRESH_BUFFER_MS = 60_000;
const request = createAppRequest();

export interface OpenPlatformTokenResult {
  accessToken: string;
  expiresAt: number;
}

export type OpenPlatformTokenContext = Pick<
  CurrentUser,
  'tenantUuid' | 'memberUuid'
> & {
  organizationUuid?: string;
};

export const toOpenPlatformTokenContext = (
  value: unknown,
): OpenPlatformTokenContext | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const tenantUuid = normalizeText(record.tenantUuid);
  const organizationUuid = normalizeText(record.organizationUuid);
  const memberUuid = normalizeText(record.memberUuid);
  if (!tenantUuid || !organizationUuid || !memberUuid) {
    return null;
  }

  return { tenantUuid, organizationUuid, memberUuid };
};

type CachedOpenPlatformToken = OpenPlatformTokenResult & {
  cacheKey: string;
};

let tokenRequest: Promise<OpenPlatformTokenResult> | null = null;
let tokenRequestCacheKey = '';

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const getCacheKey = (currentUser: OpenPlatformTokenContext): string =>
  [
    normalizeText(currentUser.tenantUuid),
    normalizeText(currentUser.organizationUuid),
    normalizeText(currentUser.memberUuid),
  ].join('|');

export const clearOpenPlatformToken = (): void => {
  sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
  tokenRequest = null;
  tokenRequestCacheKey = '';
};

export const getCachedOpenPlatformToken = (
  currentUser: OpenPlatformTokenContext,
): OpenPlatformTokenResult | null => {
  const cacheKey = getCacheKey(currentUser);
  try {
    const rawValue = sessionStorage.getItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const value = JSON.parse(rawValue) as CachedOpenPlatformToken;
    if (
      value.cacheKey !== cacheKey ||
      !normalizeText(value.accessToken) ||
      typeof value.expiresAt !== 'number' ||
      value.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
    ) {
      clearOpenPlatformToken();
      return null;
    }

    return { accessToken: value.accessToken, expiresAt: value.expiresAt };
  } catch {
    clearOpenPlatformToken();
    return null;
  }
};

export const getOpenPlatformToken = (
  currentUser: OpenPlatformTokenContext,
): string | null =>
  getCachedOpenPlatformToken(currentUser)?.accessToken || null;

export const fetchOpenPlatformToken = async (
  currentUser: OpenPlatformTokenContext,
): Promise<OpenPlatformTokenResult> => {
  const cacheKey = getCacheKey(currentUser);
  if (
    !normalizeText(currentUser.tenantUuid) ||
    !normalizeText(currentUser.organizationUuid) ||
    !normalizeText(currentUser.memberUuid)
  ) {
    clearOpenPlatformToken();
    throw new Error('当前会话缺少开放平台 Token 所需信息');
  }

  const cachedToken = getCachedOpenPlatformToken(currentUser);
  if (cachedToken) {
    return cachedToken;
  }

  if (tokenRequest && tokenRequestCacheKey === cacheKey) {
    return tokenRequest;
  }

  tokenRequestCacheKey = cacheKey;
  tokenRequest = request<OpenPlatformTokenResult>('/bff/open-platform/token', {
    method: 'GET',
  })
    .then((result) => {
      if (
        !normalizeText(result?.accessToken) ||
        typeof result?.expiresAt !== 'number'
      ) {
        throw new Error('开放平台令牌响应无效');
      }

      sessionStorage.setItem(
        OPEN_PLATFORM_TOKEN_STORAGE_KEY,
        JSON.stringify({ ...result, cacheKey }),
      );
      return result;
    })
    .finally(() => {
      tokenRequest = null;
      tokenRequestCacheKey = '';
    });

  return tokenRequest;
};
